import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizIcon, WizPopUp} from '@wizeoo/akm-component-lib';
import { BusinessException, CssRender, EntityModificationPack, Style } from '@wizeoo/akm-message-lib';
import { CommitCmd } from '../../command/em/commit.cmd';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { StyleVO } from '../../vo/workingdata/style.vo';
import { StyleLineVO } from '../../vo/workingdata/styleline.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { GridApi } from '@ag-grid-community/core';
import { ColumnStyleTrio, EM_ACTION, FeiKeyDef, WidgetLine } from '@wizeoo/akm-compute-lib';
import { cloneDeep } from 'lodash-es';
import { CssRenderVO } from '../../vo/workingdata/cssrender.vo';

/**
 * rendu css décomposé
 */
export interface styleTrio  {
 backgroundColor: string;
 color: string;
 bold: boolean;
}

/**
 * Manager des styles
 */
export class StyleEngine {

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model;
  }


  // constantes de styles modifiables pour les conditions de rendu
  public static STYLE_FONT_COLOR: string = 'color';
  public static STYLE_BACKGROUND_COLOR: string = 'backgroundColor';
  public static STYLE_FONT_WEIGHT: string = 'fontWeight';

  public static STYLE_DEFAULT_BAR_COLOR: string = '#501F5B'; // couleur des barres, standard (violet)

  public static STYLE_DEFAULT_FONT_COLOR: string = '#1A1A1A'; // couleur de police par défaut pour un header de colonne
  public static STYLE_DEFAULT_BACKGROUND_COLOR: string = '#e9e9e9'; // couleur de background par défaut pour un header de colonne
  public static STYLE_DEFAULT_WIDGET_BACKGROUND_COLOR: string = '#FFFFFFFF'; // couleur de background par défaut pour un widget
  
  public static STYLE_DEFAULT_CELL_FONT_COLOR: string = 'unset'; // couleur de police par défaut pour un header de colonne
  public static STYLE_DEFAULT_CELL_BACKGROUND_COLOR: string = 'unset'; // couleur de background par défaut pour un header de colonne

  public static STYLE_DEFAULT_ICON_COLOR: string = '#5A4176'; // Couleur par défaut des icones Baseline
  public static STYLE_DEFAULT_FD_ICON_COLOR: string = '#2828a5'; // Couleur par défaut des icones Freedata
  public static STYLE_DEFAULT_GLOBAL_ICON_COLOR: string = '#F58022'; // Couleur par défaut des icones globales
   
  public static STYLE_DEFAULT_SCEN_COLOR: string = '#F58022'; // couleur de la marque de scenario

public static STYLE_DEFAULT_VIEW_BACKGROUND_COLOR: string = '#FCFCFC'; // couleur de background par défaut pour un header de colonne

  /**
   * sur changement des styles, alimentation du modèle
   * @param styles: Style[]
   */
  public buildMap(styles: Style[]): void {
    const flatList: StyleVO[] = styles.map((sty) => new StyleVO(this._model, sty));
    this.model.styleList = flatList;

    const styleMap: Map<string, Map<string, StyleVO>> = new Map<string, Map<string, StyleVO>>();

    flatList.forEach((svo: StyleVO) => {
      if (!styleMap.has(svo.visionCode)) {
        styleMap.set(svo.visionCode, new Map<string, StyleVO>());
      }
      styleMap.get(svo.visionCode).set(svo.code, svo);
    });
    this.model.styleMap = styleMap;
  }

  /**
   * Clone d'un style pour édition
   * @param sty le style à cloner
   * @return StyleVO,  Un clone
   */
  public clone(sty: StyleVO): StyleVO {
    const clone = new StyleVO(this._model, cloneDeep(sty.source));
    return clone;
  }

  /**
   * Clone d'une ligne de style
   * @param measure la ligne à cloner
   * @return StyleLineVO,  Un clone
   */
  public cloneCondition(styl: StyleLineVO): StyleLineVO {
    const clone = new StyleLineVO(this._model, cloneDeep(styl.source));
    return clone;
  }

  /** création d'un nouveau style */
  public buildFake(): StyleVO {
    const result: StyleVO = new StyleVO(this._model);
    result.domain = this.model.domainCode;
    result.businessUnitCode = this.model.businessUnitCode;
    result.visionCode = this.model.visionCode ? this.model.visionCode : ViewData.wildcardDomain;
    result.owner = this.model.connectedUser.mail;
    result.linesVO = [];
    return result;
  }

  /**
   * création d'une nouvelle condition
   * @param clone source
   * @returns la nouvelle ligne
   */
  public buildFakeLine(clone: StyleVO): StyleLineVO {
    const result: StyleLineVO = new StyleLineVO(this._model);
    result.domain = clone.domain;
    result.businessUnitCode = clone.businessUnitCode;
    result.key = clone.code;
    result.order = clone.linesVO.length;
    result.comparator = null;
    result.formula = null;
    result.css = null;
    return result;
  }

  /**
   * Récupération d'un style par son couple visionCode / code
   * @param visionCode la vision de rattachement de ce style
   * @param code Le code du style
   */
  public getStyle(visionCode: string, code: string): StyleVO {
    let svo: StyleVO = null;
    if (this.model.styleMap && this.model.styleMap.get(visionCode)) {
      svo = this.model.styleMap.get(visionCode).get(code);
    }
    return svo;
  }

  /**
   * suppression de l'instance d'un styleVO
   * @param visionCode la vision de rattachement de ce style
   * @param code Le code du style
   */
  private removeStyle(visionCode: string, code: string): void {
    const toDie: StyleVO = this.getStyle(visionCode, code);
    this.model.styleList.splice(this.model.styleList.indexOf(toDie), 1);
    this.model.styleMap.get(visionCode).delete(code);
  }

  /**
   * ajout de l'instance d'un styleVO
   * @param vo styleVO
   */
  private addStyle(vo: StyleVO): void {
    this.model.styleList.push(vo);
    if (!this.model.styleMap.has(vo.visionCode)) {
      this.model.styleMap.set(vo.visionCode, new Map<string, StyleVO>());
    }
    this.model.styleMap.get(vo.visionCode).set(vo.code, vo);
  }

  /**
   * create/ update d'un style
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: StyleVO, from: StyleVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack && pack.entries.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this._model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(); // retour en vue
    }
  }

  /**
   * delete d'un style
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: StyleVO, from: StyleVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this._model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: StyleVO = cmd.sourceVO[0] as StyleVO;
      // une seule modification à la fois, packEntry donne l'objet
      const style: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = style; // le clone mis à jour
            this.getStyle(vo.visionCode, vo.code).source = style;
            break;
          case EM_ACTION.CREATE:
            vo.source = style;
            this.addStyle(vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = style;
            this.removeStyle(vo.visionCode, vo.code);
            break;
        }
      }
      cmd.viewCallBack(vo); // retour en vue
    }
  }

  /* #############################################################
                    moteur d'application des styles
     ############################################################# */

  public static NO_CSS: string = 'color:unset; background-color:unset; font-weight:unset'; // neutralisation du style
  /** rendu pas de style en mode css */
  private static NO_CSS_AS_CSS: any = null;

  /**
   * conversion de la chaine de texte en CSS
   *
   * @param css au format string
   * @returns css vrai
   */
  public stringToCss(css: string): any {
    // console.log(css);
    if (css === '') {
      return null;
    }
    // Example: value = "background-color:blue";

    const keyValuePairs = {};
    if (!!css) {
      try {
        const properties = css.split(';');
        properties.forEach((o) => {
          const key = String(o.split(':')[0]).trim();
          const value = String(o.split(':')[1]).trim();
          if (key.length > 0) {
            keyValuePairs[key] = value;
          }
        });
      } catch (e: any) {}
    }

    return keyValuePairs;
  }

  /**
   * application de "pas de style"
   *
   * @param params les paramètres de la cellule, contiennent la valeur, la colonne, le pivot, le style... bref, tout
   * @returns le CSS à appliquer
   */
  public noCssRenderer(params: any): any {
      if (!StyleEngine.NO_CSS_AS_CSS) {
        StyleEngine.NO_CSS_AS_CSS = this.stringToCss(StyleEngine.NO_CSS); // si aucun style défini (ne devrait pas arriver, pas de raison de passer ici sans style)
      }  
      return StyleEngine.NO_CSS_AS_CSS;
  }

  /** stockage des styles statiques déjà construits, pour ne pas les régénérer à chaque fois */
  public styleMap: Map<string, any> = new Map();


  public styleRevertRenderer(params: any): any {
    
    const line: WidgetLine = params.data; // ligne source (la mesure en fait)
    const fkd: FeiKeyDef = params.colDef.fkd; // fkd (une mesure fake, c'est une fait un code FDV ou REVERT_MESURE_COLUMN)

    let result: any = this.stringToCss(StyleEngine.NO_CSS); // si aucun style défini (ne devrait pas arriver, pas de raison de passer ici sans style)
    
    if (!!line?.wcio?.sty) { // style conditionnel
      if(params.colDef?.fkd?.code === ViewData.REVERT_MESURE_COLUMN) { // c'est la 1ère colonne, elle porte le nom des mesures
        // pas de style conditionnel sur la 1ère colonne
      } else {
        
        let parts: string[] = line.wcio.sty.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code 
        const style: StyleVO = this.getStyle(parts[0], parts[1]);
        
        
        let realLine: WidgetLine = params.colDef.widgetLine;
        // console.log('styleRevertRenderer', parts, realLine, params.value);
        if (!!style) {//} && params.value !== null) {
          // pas d'application sur les cellules vides
          result = this.model.widgetEngine.computeStyle(params.colDef.widget.fei, style, params, result, realLine);
        }
      } 

    } else if (!!line?.wcio?.csty) { // application du style par défaut 
      let styleKey: string = [line.key, line.wcio.csty].join(ViewData.FD_PARTS_SEPARATOR);
      if(!this.styleMap.has(styleKey)) {
        const parts: string[] = line.wcio.csty.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code
        let cssStr: string = 'color:' + parts[1] + '; backgroundColor:' + parts[0] ; 
        if(!!parts[2]) {
            cssStr += '; font-weight:' + parts[2];
          } else {
            cssStr += '; font-weight:unset';
          }
        const css = this.stringToCss(cssStr);
        this.styleMap.set(styleKey, css); // TODO prendre dans styleMap si existe    
      } 
      result = this.styleMap.get(styleKey);
    }

    return result;
    
  }

  /**
   * application des conditions du style sur la valeur passée en paramètre
   *
   * @param params les paramètres de la cellule, contiennent la valeur, la colonne, le pivot, le style... bref, tout
   * @returns le CSS à appliquer
   */
  public styleRenderer(params: any): any {
    let result: any = this.stringToCss(StyleEngine.NO_CSS); // si aucun style défini (ne devrait pas arriver, pas de raison de passer ici sans style)
    
    if(params.node.selected) { // pas de style sur les sélections
      return result;
    } 

    let useAggRow: boolean = params.colDef?.widget?.context?.pmd;
    let line: WidgetLine = params.data;

    if(!line) { // pas de ligne...
      console.log('styleRenderer error', params);
      return null;
    }

    // gestion de l'héritage de style
    try {
      if(!!params.colDef.wcio?.headInherit) {
        let daddyStyle = params.column.parent.getOriginalColumnGroup().getColGroupDef().style;
        if(daddyStyle) {
          params.colDef.defaultAggStyleRenderer = daddyStyle;
          // console.log('foot style override', params.colDef, result);
        }
      }
    } catch(e: any) {

    }


    // construction éventuelle des styles
    if(line.isTotalLine || (line.isSubTotalLine && useAggRow)) { // construction du style si nécessaire mode total
      if(!this.styleMap.has(params.colDef.defaultAggStyleRenderer)) {
        const parts: string[] = params.colDef.defaultAggStyleRenderer.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code
        const cssStr: string = 'color:' + parts[1] + '; backgroundColor:' + parts[0] +'; font-weight:unset'; 
        const css = this.stringToCss(cssStr);
        this.styleMap.set(params.colDef.defaultAggStyleRenderer, css);
      } 
      
      

      result = this.styleMap.get(params.colDef.defaultAggStyleRenderer);

      // if(line.isTotalLine) {
      //   console.log('foot style', params.colDef, result);
      // }

      if(line.subTotalLevel > 0) { // neutralisation du style sur les sous-totaux multiples
        // console.log('styleRenderer', params, result);
        return null;
      }
      
    } else { // pivot sans détail ou grid simple
      if (!!params.colDef.styleRenderer) {
        // console.log('style ?',  params.colDef.styleRenderer)
        let parts: string[] = params.colDef.styleRenderer.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code 
        const style: StyleVO = this.getStyle(parts[0], parts[1]);
        if (!!style) { //  && params.value !== null) {
          // pas d'application sur les cellules vides
          result = this.model.widgetEngine.computeStyle(params.colDef.widget.fei, style, params, result);
        }
      } else if (!!params.colDef.defaultStyleRenderer) { // application du style par défaut 
        if(!this.styleMap.has(params.colDef.defaultStyleRenderer)) {
          const parts: string[] = params.colDef.defaultStyleRenderer.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code
          let cssStr: string = 'color:' + parts[1] + '; backgroundColor:' + parts[0]; 
          if(!!parts[2]) {
            cssStr += '; font-weight:' + parts[2];
          } else {
            cssStr += '; font-weight:unset';
          }
          const css = this.stringToCss(cssStr);
          this.styleMap.set(params.colDef.defaultStyleRenderer, css);
          
        } 
        result = this.styleMap.get(params.colDef.defaultStyleRenderer);
      }
    }

    let firstStyle: boolean = false; // pas encore créé
    let newStyle: boolean = false; // différent

    let columnTrio: ColumnStyleTrio = line.columnStyleMap?.get(params.column);
    let currentStyle: any = null; // le style en cours
    if(!!columnTrio) {
      if(line.isTotalLine) {
        currentStyle = columnTrio.total;
      } else if(line.isSubTotalLine && useAggRow) {
        currentStyle = columnTrio.subTotal;
      } else {
        currentStyle = columnTrio.basic;
      }
    }

    if(!!currentStyle && currentStyle !== result) { // seulement si columnStyleMap, sinon premier passage
      if(currentStyle.backgroundColor !== result.backgroundColor) {
        newStyle = true;
      } else if(currentStyle.color !== result.color) {
        newStyle = true;
      } else if(currentStyle.fontWeight !== result.fontWeight) {
        newStyle = true;
      }
    } else { // pas encore créé
      firstStyle = true;
    }
    
    if(firstStyle || newStyle) {
      if(!line.columnStyleMap) {
        line.columnStyleMap = new Map();
      }
      if(!line.columnStyleMap.has(params.column)) {
        line.columnStyleMap.set(params.column, {basic: null, subTotal: null, total: null});
      }
      // mise à jour du style sur la colonne
      columnTrio = line.columnStyleMap?.get(params.column);
      if(line.isTotalLine) {
        columnTrio.total = result;
      } else if(line.isSubTotalLine && useAggRow) {
        columnTrio.subTotal = result;
      } else {
        columnTrio.basic = result;
      }
    }

    

    // TODO TODO pas si consolidation
    // line.columnStyleMap.set(params.column, result);

    if(newStyle) { // seulement si columnStyleMap, sinon premier passage
      // console.log('val', params.column, current, result, params.node);
      setTimeout(() => {
            // TODO seulement les cellules de la ligne modifiée, et 
      // (params.api as GridApi).redrawRows({rowNodes: [params.nodes] });
      (params.api as GridApi).refreshCells({ rowNodes: [params.node], force: true });
      // (params.api as GridApi).refreshCells({force: true});
      }, 0);
    }
  
    return result;
  }







  /* #############################################################
            gestion de l'injection css
     ############################################################# */

    /**
     * Clone d'un rendu
     * @param cssRender le style à cloner
     * @return CssRenderVO,  Un clone
     */
    public cloneCssRender(cssVO: CssRenderVO): CssRenderVO {
      const clone = new CssRenderVO(this._model, cloneDeep(cssVO.source));
      return clone;
    }

    /** création d'un nouveau style */
    public buildFakeCssRender(): CssRenderVO {
      const result: CssRenderVO = new CssRenderVO(this._model);
      result.domain = this.model.domainCode;
      result.businessUnitCode = this.model.businessUnitCode;
      result.vision = this.model.visionCode ? this.model.visionCode : ViewData.wildcardDomain;
      return result;
    }

  /**
   * create/ update d'un CssRenderVO
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public mergeCSS(to: CssRenderVO, from: CssRenderVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack && pack.entries.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCallCSS.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(); // retour en vue
    }
  }

  /**
   * delete
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public removeCSS(to: CssRenderVO, from: CssRenderVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCallCSS.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCallCSS(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: CssRenderVO = cmd.sourceVO[0] as CssRenderVO;

      // une seule modification à la fois, packEntry donne l'objet
      const cssRender: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = cssRender; // le clone mis à jour
            this.getCssRender(vo.vision, vo.code).source = cssRender;
            break;
          case EM_ACTION.CREATE:
            vo.source = cssRender;
            this.addCssRender(vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = cssRender;
            this.removeCssRender(vo.businessUnitCode, vo.code);
            break;
        }
      }
      cmd.viewCallBack(vo, operation); // retour en vue
    }
  }


    public newCssRenderVO(source?: CssRender): CssRenderVO {
        return new CssRenderVO(this.model, source);
    }

    public getCssRenderList(): CssRenderVO[] {
        return this._model.cssrenderList;
    }

    public getCssRenderMap(): Map<string, Map<string, CssRenderVO>> {
        return this._model.cssrenderMap;
    }
    
    public setCssRenderMap(map: Map<string, Map<string, CssRenderVO>>) {
        this._model.cssrenderMap = map;
    }

    /**
     * Récupération d'un CssRender par son couple vision / code
     * @param visionCode la vision de rattachement de ce style
     * @param code Le code du format
     */
    public getCssRender(visionCode: string, code: string): CssRenderVO {
        let svo: CssRenderVO = null;
        if (this.getCssRenderMap() && this.getCssRenderMap().get(visionCode)) {
            svo = this.getCssRenderMap().get(visionCode).get(code);
        }
        return svo;
    }

    /**
     * suppression de l'instance d'un CssRender
     * @param visionCode la vision de rattachement de ce style
     * @param code Le code du format
     */
    public removeCssRender(visionCode: string, code: string): void {
        const toDie: CssRenderVO = this.getCssRender(visionCode, code);
        this._model.cssrenderList.splice(this._model.cssrenderList.indexOf(toDie), 1);
        this.getCssRenderMap().get(visionCode).delete(code);
    }

    /**
     * ajout de l'instance d'un CssRenderVO
     * @param vo CssRenderVO
     */
    public addCssRender(vo: CssRenderVO): void {
        if (!this._model.cssrenderList) {
            this._model.cssrenderList = [];
        }
        this._model.cssrenderList.push(vo);
        if (!this.getCssRenderMap()) {
            this.setCssRenderMap(new Map<string, Map<string, CssRenderVO>>());
        }
        if (!this.getCssRenderMap().has(vo.vision)) {
            this.getCssRenderMap().set(vo.vision, new Map<string, CssRenderVO>());
        }
        this.getCssRenderMap().get(vo.vision).set(vo.code, vo);
    }

    /**
     * Alimentation des formats du modèle
     * @param formats: Format[]
     */
    public feedCssRenders(cssRenders: CssRender[]): void {
        const flatList: CssRenderVO[] = cssRenders.map((f) => this.newCssRenderVO(f));

        // injection dans la feuille de style
        flatList.forEach((fvo: CssRenderVO) => {             
          this.cssMaker(fvo);
        });

        if (this._model.cssrenderList == null || this._model.cssrenderList.length === 0) {
            flatList.forEach((fvo: CssRenderVO) => {
                this.addCssRender(fvo);
            });
        } else {
            flatList.forEach((fvo: CssRenderVO) => {
                const fvoIndex = this._model.cssrenderList.findIndex(element => element.vision === fvo.vision && element.code === fvo.code);
                if (fvoIndex < 0) {
                    this.addCssRender(fvo);
                } else {
                    this._model.cssrenderList[fvoIndex] = fvo;
                    this.getCssRenderMap().get(fvo.vision).set(fvo.code, fvo);
                }
            });
        }
    }

  /**
   * injection d'un style
   * @param className 
   * @param css 
   */
  public cssMaker(cssR: CssRenderVO) {
    let style = document.createElement('style');
    style.innerHTML = cssR.css;
    document.head.appendChild(style); 
}

  /**
   * rendu couleur depuis un style ou défaut
   * @param style 
   * @param backgroundColor 
   * @param color 
   * @param bold 
   * @returns 
   */
  public colorSet(style: string, backgroundColor: string, color: string, bold: boolean): {backgroundColor: string, color: string, bold: boolean} {
    let res: {backgroundColor: string, color: string, bold: boolean} = {backgroundColor, color, bold};

    if(!!style) {
      const cleanStr: string[] = style.replace(new RegExp(ViewData.CONTEXT_SEPARATOR, 'g'), '').split(ViewData.KEY_SEPARATOR);
      if(cleanStr.length > 0) {
        backgroundColor = cleanStr[0];
        if (!!backgroundColor && backgroundColor !=='unset') {
          res.backgroundColor = backgroundColor;
        }
      }
      if(cleanStr.length > 1) {
        color = cleanStr[1];
        if (!!color && color !=='unset') {
          res.color = color;
        }
      }
      if(cleanStr.length > 2) {
        bold = cleanStr[2] === 'true';
        res.bold = bold;
      }
    }
    return res;
  }

}
